import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * My Clock Status — shows whether the current user is clocked in/out,
 * today's hours, and lets them clock in/out from a single place.
 */
export function MyClockStatusDialog({ open, onOpenChange, onClockAction }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/time/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      setStatus(await res.json());
    } catch {
      toast.error('Could not load your clock status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadStatus();
  }, [open]);

  const isClockedIn = status?.is_clocked_in;

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const shiftElapsedHours = () => {
    if (!status?.current_shift_start) return null;
    try {
      const start = new Date(status.current_shift_start);
      const ms = Date.now() - start.getTime();
      return (ms / 3600000).toFixed(2);
    } catch {
      return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="clock-status-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" /> My Clock Status
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin h-8 w-8 border-2 border-purple-600 border-t-transparent rounded-full"></div>
          </div>
        ) : status ? (
          <div className="space-y-4">
            <div
              className={`rounded-xl border-2 p-4 ${
                isClockedIn
                  ? 'bg-green-50 border-green-300'
                  : 'bg-gray-50 border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {isClockedIn ? (
                  <CheckCircle2 className="w-8 h-8 text-green-600 flex-shrink-0" />
                ) : (
                  <XCircle className="w-8 h-8 text-gray-500 flex-shrink-0" />
                )}
                <div>
                  <div className="font-bold text-lg">
                    {isClockedIn ? 'You are Clocked IN' : 'You are Clocked OUT'}
                  </div>
                  {isClockedIn && status.current_shift_start && (
                    <div className="text-sm text-gray-700">
                      Started at {fmtTime(status.current_shift_start)} · {shiftElapsedHours()} hrs so far
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">Today</div>
                <div className="text-xl font-bold text-green-700">{status.today_hours || 0}</div>
                <div className="text-[10px] text-gray-400">hours</div>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">This Week</div>
                <div className="text-xl font-bold text-green-700">{status.week_hours || 0}</div>
                <div className="text-[10px] text-gray-400">hours</div>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">Pay Period</div>
                <div className="text-xl font-bold text-green-700">{status.pay_period_hours || 0}</div>
                <div className="text-[10px] text-gray-400">hours</div>
              </div>
            </div>

            {onClockAction && (
              <Button
                className={`w-full ${
                  isClockedIn
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                onClick={() => {
                  onOpenChange(false);
                  onClockAction(isClockedIn ? 'clock-out' : 'clock-in');
                }}
                data-testid="clock-toggle-btn"
              >
                {isClockedIn ? 'Clock Out Now' : 'Clock In Now'}
              </Button>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default MyClockStatusDialog;
